/**
 * Chrome tokens — the shared scifi-vocabulary design tokens used by both
 * the spectator overlay (visual-engine) and the app-level base components
 * (Card, Button, Dialog, Input, Badge, Chip, ...) in `@pilot-protocol/ui`.
 *
 * Naming: `--pp-*` (no `-ov-` infix). The overlay used to scope its
 * tokens under `--pp-ov-*` while it was the only consumer; promoting to
 * `--pp-*` reflects that this IS the canonical design vocabulary for the
 * whole app now.
 *
 * Wire the comp-pulled theme via any of:
 *
 *   1. Override the `tokens` prop on a consumer that accepts one
 *      (per-instance).
 *   2. Set the matching `--pp-*` variables on an ancestor element
 *      (global / per-screen).
 *   3. Ship a `ChromeTokens` JSON skin and swap it at runtime for the
 *      `replay_overlay` 2D-UI cosmetic (voxel-cosmetics-plan §1).
 *
 * Defaults stay deliberately neutral / dark so the 3D scene + content
 * read as the hero. Re-tune values when style comps land — but the
 * shape primitives (Panel / Pip / notched corners) shouldn't change.
 */

export interface ChromeTokens {
  // ── Colors ────────────────────────────────────────────────────────
  /** Panel interior fill. Default transparent — the retro-scifi look
   *  relies on hairline outlines, not filled panels. Panels also accept
   *  this token (audit fix — was previously hardcoded). */
  bg: string;
  /** Stronger panel background for the top strip / pinned rows. */
  bgStrong: string;
  /** Primary text. */
  text: string;
  /** Secondary / less-emphasized text. */
  textMuted: string;
  /** Text color when laid over an accent-colored chip / tag. Audit fix:
   *  previously `#000` was hardcoded at every callsite, which breaks
   *  under dark-accent theme skins. */
  textOnAccent: string;
  /** Hairline border / divider color. */
  border: string;
  /** Page-level background grid color (very subtle by default). */
  gridColor: string;
  /** Side A accent. */
  accentA: string;
  /** Side B accent. */
  accentB: string;
  /** Heat warn threshold color. */
  warn: string;
  /** Heat danger / armor exposed color — the chromatic punch in the
   *  retro-scifi palette; reserved for genuine alarm. */
  danger: string;
  /** Positive / "alive" indicator. Muted in the retro-scifi palette
   *  (no bright green); use sparingly. */
  ok: string;

  // ── Spacing + radius ──────────────────────────────────────────────
  /** Inter-stack gap inside a panel (label row → control row, etc.). */
  gap: string;
  /** Panel interior padding (CSS shorthand). */
  padding: string;
  /** CSS border-radius. Defaults to 0 — corners come from the
   *  chamfered notch (see `cornerNotch`). */
  radius: string;
  /** Size of the 45° corner chamfer applied to panel outlines, in px.
   *  Set to "0" to disable the chamfer and fall back to square corners. */
  cornerNotch: string;
  /** Background grid cell size (e.g. "32px"). */
  gridSize: string;
  /** Distance from viewport edge to anchored panels (overlay corners,
   *  app drawer rails). Replaces the ad-hoc 8/12/16 numbers that used
   *  to live in component code. */
  edgeGutter: string;
  /** Vertical gap between stacked corner-anchored panels (a panel and
   *  the panel below it sharing the same anchor edge). */
  stackGap: string;
  /** Side length of small circular indicators (score dots, evasion pips,
   *  accent dots, status pips). Audit fix — previously 8/10 magic px. */
  pipSize: string;

  // ── Typography ────────────────────────────────────────────────────
  fontFamily: string;
  fontSizeSm: string;
  fontSizeMd: string;
  fontSizeLg: string;
  /** Hero text size — used by accolade flash + similar high-drama
   *  moments. Audit fix — accolade was using `fontSizeLg` (13px), too
   *  small for a hero element. */
  fontSizeHero: string;
  weightNormal: number | string;
  weightBold: number | string;
  /** Letter-spacing applied to ALL-CAPS labels (badges, section
   *  headers). Tightens or loosens the retro display feel. */
  trackingCaps: string;

  // ── Effects ───────────────────────────────────────────────────────
  /** Bottom-edge fade mask reused by scrolling / decaying feeds
   *  (telemetry rows, reasoning overflow). Stored as a complete
   *  `mask-image` value so consumers paste it verbatim. */
  fadeMaskBottom: string;
}

/**
 * Retro-scifi defaults: pure black, white hairlines, chamfered corners,
 * sparse grid. Red is the only chromatic punch (reserved for `danger`).
 * Side accents are muted washes so the chromatic vocabulary stays small.
 * Tune freely when comps land — every value is intentionally swappable.
 */
export const DEFAULT_CHROME_TOKENS: ChromeTokens = {
  bg: "rgba(0, 0, 0, 0.55)",
  bgStrong: "rgba(0, 0, 0, 0.86)",
  text: "#e8e8e8",
  textMuted: "#7a7a7a",
  textOnAccent: "#000000",
  border: "#5c5c5c",
  gridColor: "rgba(120, 120, 120, 0.10)",
  accentA: "#9ec5d6",
  accentB: "#d6a07a",
  warn: "#d4a64a",
  danger: "#ff3a3a",
  ok: "#a8a8a8",
  gap: "6px",
  padding: "8px 10px",
  radius: "0",
  cornerNotch: "10px",
  gridSize: "32px",
  edgeGutter: "12px",
  stackGap: "12px",
  pipSize: "8px",
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSizeSm: "9px",
  fontSizeMd: "11px",
  fontSizeLg: "13px",
  fontSizeHero: "22px",
  weightNormal: 400,
  weightBold: 600,
  trackingCaps: "0.12em",
  fadeMaskBottom:
    "linear-gradient(to bottom, #000 0, #000 90%, transparent 100%)",
};

/** Map a partial token override onto the full set. */
export function resolveChromeTokens(
  overrides?: Partial<ChromeTokens>,
): ChromeTokens {
  return overrides
    ? { ...DEFAULT_CHROME_TOKENS, ...overrides }
    : DEFAULT_CHROME_TOKENS;
}

/**
 * Translate the token record into a `style` object that sets every
 * `--pp-*` CSS variable. Apply on the chrome root so descendants pick
 * them up via `var()` references.
 */
export function chromeTokensToCssVars(
  t: ChromeTokens,
): Record<string, string> {
  return {
    "--pp-bg": t.bg,
    "--pp-bg-strong": t.bgStrong,
    "--pp-text": t.text,
    "--pp-text-muted": t.textMuted,
    "--pp-text-on-accent": t.textOnAccent,
    "--pp-border": t.border,
    "--pp-grid-color": t.gridColor,
    "--pp-accent-a": t.accentA,
    "--pp-accent-b": t.accentB,
    "--pp-warn": t.warn,
    "--pp-danger": t.danger,
    "--pp-ok": t.ok,
    "--pp-gap": t.gap,
    "--pp-padding": t.padding,
    "--pp-radius": t.radius,
    "--pp-corner-notch": t.cornerNotch,
    "--pp-grid-size": t.gridSize,
    "--pp-edge-gutter": t.edgeGutter,
    "--pp-stack-gap": t.stackGap,
    "--pp-pip-size": t.pipSize,
    "--pp-font-family": t.fontFamily,
    "--pp-font-sm": t.fontSizeSm,
    "--pp-font-md": t.fontSizeMd,
    "--pp-font-lg": t.fontSizeLg,
    "--pp-font-hero": t.fontSizeHero,
    "--pp-weight-normal": String(t.weightNormal),
    "--pp-weight-bold": String(t.weightBold),
    "--pp-tracking-caps": t.trackingCaps,
    "--pp-fade-mask-bottom": t.fadeMaskBottom,
  };
}
